// src/pages/SkillAssignmentPage.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Snackbar, Alert, Chip, LinearProgress, FormControl, InputLabel, Select,
  MenuItem, Checkbox, List, ListItem, ListItemText, ListItemButton,
  Divider, Stack, Tabs, Tab, ButtonGroup, TextField, Menu, ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon, Remove as RemoveIcon, Refresh as RefreshIcon,
  SwapHoriz as SwapIcon, FileCopy as CopyIcon, AssignmentInd as AssignmentIcon,
  Person as PersonIcon, Delete as DeleteIcon, ViewModule as ViewModuleIcon,
  WorkOutline as SkillIcon, ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon, ViewList as ViewListIcon,
  ViewColumn as ViewColumnIcon, AccountTree as AccountTreeIcon,
  ViewKanban as ViewKanbanIcon, ContentCut as CutIcon, ContentPaste as PasteIcon
} from '@mui/icons-material';
import { ApiContext } from '../contexts/ApiContext';
import { usePermissions } from '../hooks/usePermissions';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SkillAssignmentPage() {
  const { agents, skills } = useContext(ApiContext);
  const { canAction } = usePermissions();

  const [agentsList, setAgentsList] = useState([]);
  const [skillsList, setSkillsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Estados para asignación de skills a agentes
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [skillsToAssign, setSkillsToAssign] = useState([]);
  const [agentSearchTab1, setAgentSearchTab1] = useState('');
  const [filterSkillTab1, setFilterSkillTab1] = useState('');
  const [filterSupervisor, setFilterSupervisor] = useState('');
  const [filterManager, setFilterManager] = useState('');
  const [skillSearchAssign, setSkillSearchAssign] = useState('');
  const [skillSearchRemove, setSkillSearchRemove] = useState('');

  // Estados para asignación de agentes a skills
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [agentsToAssign, setAgentsToAssign] = useState([]);
  const [agentSearchTab2, setAgentSearchTab2] = useState('');

  // Estados para operaciones de skills
  const [skillsToRemove, setSkillsToRemove] = useState([]);
  const [sourceSkill, setSourceSkill] = useState('');
  const [targetSkill, setTargetSkill] = useState('');

  // Estados para vista drag & drop
  const [draggedAgents, setDraggedAgents] = useState([]);
  const [draggedFromSkill, setDraggedFromSkill] = useState(null);
  const [expandedSkills, setExpandedSkills] = useState([]);
  const [dragOverSkill, setDragOverSkill] = useState(null);
  const [searchDragView, setSearchDragView] = useState('');
  const [selectedAgentsDrag, setSelectedAgentsDrag] = useState({}); // { 'panel': [...], 'skill_123': [...] }
  const [isDragging, setIsDragging] = useState(false);
  const [dragModeCopy, setDragModeCopy] = useState(false);
  const dragModeCopyRef = useRef(false); // Ref para rastrear el modo durante el drag
  const [filterSkillDrag, setFilterSkillDrag] = useState('all'); // 'all' | 'unassigned' | skillId
  const [lastSelectedAgent, setLastSelectedAgent] = useState(null); // Para selección con Shift
  const [activeSelectionContext, setActiveSelectionContext] = useState(null); // 'panel' | 'skill_123'
  const [skillViewMode, setSkillViewMode] = useState('grid'); // 'grid' | 'tree' | 'kanban' | 'network'
  const [expandedCampaigns, setExpandedCampaigns] = useState([]); // Para vista árbol
  const [filterCampaignDrag, setFilterCampaignDrag] = useState('all'); // Filtro por campaña

  // Estados para copiar/cortar/pegar
  const [clipboard, setClipboard] = useState({ agents: [], mode: null }); // mode: 'copy' | 'cut'
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, skillId: null });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const show = (m, s='success') => setSnackbar({ open: true, message: m, severity: s });
  const closeSnack = () => setSnackbar(prev => ({ ...prev, open: false }));

  const loadData = async () => {
    try {
      setLoading(true);
      const [agentsRes, skillsRes] = await Promise.all([
        agents.getAll(),
        skills.getAll()
      ]);

      const allAgents = agentsRes?.data?.agents || [];
      setAgentsList(allAgents.filter(agent => !agent.virtual));
      setSkillsList(skillsRes?.data?.skills || []);
    } catch (e) {
      console.error('Error loading data:', e);
      show(`Error cargando datos: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Obtener agentes asignados a un skill específico
  const getAgentsInSkill = (skillId) => {
    return agentsList.filter(agent =>
      Array.isArray(agent.skills) &&
      agent.skills.some(s => s.id_skill === skillId && s.status === 1)
    );
  };

  const agentNameLookup = React.useMemo(() => {
    const map = new Map();
    agentsList.forEach(agent => {
      if (agent.agent) {
        map.set(String(agent.agent), agent.name || agent.agent);
      }
    });
    return map;
  }, [agentsList]);

  const supervisorOptions = React.useMemo(() => {
    const seen = new Set();
    const list = [];
    agentsList.forEach(agent => {
      const raw = agent.supervisor_id || '';
      const value = typeof raw === 'string' ? raw.trim() : raw ? String(raw).trim() : '';
      if (!value || seen.has(value)) return;
      seen.add(value);
      list.push({ id: value, name: agentNameLookup.get(value) || value });
    });
    return list;
  }, [agentsList, agentNameLookup]);

  const managerOptions = React.useMemo(() => {
    const seen = new Set();
    const list = [];
    agentsList.forEach(agent => {
      const raw = agent.manager_id || '';
      const value = typeof raw === 'string' ? raw.trim() : raw ? String(raw).trim() : '';
      if (!value || seen.has(value)) return;
      seen.add(value);
      list.push({ id: value, name: agentNameLookup.get(value) || value });
    });
    return list;
  }, [agentsList, agentNameLookup]);

  const applyHierarchyFilters = (list) => {
    let filtered = list;
    if (filterSupervisor) {
      filtered = filtered.filter(agent => String(agent.supervisor_id || '').trim() === filterSupervisor);
    }
    if (filterManager) {
      filtered = filtered.filter(agent => String(agent.manager_id || '').trim() === filterManager);
    }
    return filtered;
  };

  // Obtener skills asignados a un agente específico
  const getSkillsForAgent = (agentId) => {
    const agent = agentsList.find(a => a.agent === agentId);
    if (!agent || !Array.isArray(agent.skills)) return [];
    return agent.skills.filter(s => s.status === 1);
  };

  // Asignar skills a agentes seleccionados
  const handleAssignSkillsToAgents = async () => {
    if (selectedAgents.length === 0) {
      show('Selecciona al menos un agente', 'error');
      return;
    }
    if (skillsToAssign.length === 0) {
      show('Selecciona al menos un skill para asignar', 'error');
      return;
    }

    try {
      setProcessing(true);

      console.log('[DEBUG] selectedAgents:', selectedAgents);
      console.log('[DEBUG] skillsToAssign:', skillsToAssign);

      const operations = selectedAgents.flatMap(agentId =>
        skillsToAssign.map(skillId => ({
          agent: agentId,
          id_skill: skillId,
          action: 'assign',
          priority: 1
        }))
      );

      console.log('[DEBUG] operations to send:', operations);

      const response = await agents.bulkSkillOperations(operations);
      show(response.message || 'Skills asignados exitosamente');

      setSelectedAgents([]);
      setSkillsToAssign([]);
      loadData();
    } catch (e) {
      show(`Error asignando skills: ${e.message}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Quitar skills de agentes seleccionados
  const handleRemoveSkillsFromAgents = async () => {
    if (selectedAgents.length === 0) {
      show('Selecciona al menos un agente', 'error');
      return;
    }
    if (skillsToRemove.length === 0) {
      show('Selecciona al menos un skill para quitar', 'error');
      return;
    }

    try {
      setProcessing(true);

      const operations = selectedAgents.flatMap(agentId =>
        skillsToRemove.map(skillId => ({
          agent: agentId,
          id_skill: skillId,
          action: 'unassign'
        }))
      );

      const response = await agents.bulkSkillOperations(operations);
      show(response.message || 'Skills desasignados exitosamente');

      setSelectedAgents([]);
      setSkillsToRemove([]);
      loadData();
    } catch (e) {
      show(`Error desasignando skills: ${e.message}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Asignar agentes a skills seleccionados
  const handleAssignAgentsToSkills = async () => {
    if (selectedSkills.length === 0) {
      show('Selecciona al menos un skill', 'error');
      return;
    }
    if (agentsToAssign.length === 0) {
      show('Selecciona al menos un agente para asignar', 'error');
      return;
    }

    try {
      setProcessing(true);

      const operations = selectedSkills.flatMap(skillId =>
        agentsToAssign.map(agentId => ({
          agent: agentId,
          id_skill: skillId,
          action: 'assign',
          priority: 1
        }))
      );

      const response = await agents.bulkSkillOperations(operations);
      show(response.message || 'Agentes asignados exitosamente');

      setSelectedSkills([]);
      setAgentsToAssign([]);
      loadData();
    } catch (e) {
      show(`Error asignando agentes: ${e.message}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Mover agentes de un skill a otro
  const handleMoveAgents = async () => {
    if (!sourceSkill || !targetSkill) {
      show('Selecciona skill de origen y destino', 'error');
      return;
    }
    if (sourceSkill === targetSkill) {
      show('El skill de origen y destino no pueden ser el mismo', 'error');
      return;
    }

    try {
      setProcessing(true);

      const agentsInSource = getAgentsInSkill(sourceSkill);

      if (agentsInSource.length === 0) {
        show('No hay agentes en el skill de origen', 'error');
        return;
      }

      const operations = agentsInSource.flatMap(agent => [
        {
          agent: agent.agent,
          id_skill: targetSkill,
          action: 'assign',
          priority: 1
        },
        {
          agent: agent.agent,
          id_skill: sourceSkill,
          action: 'unassign'
        }
      ]);

      const response = await agents.bulkSkillOperations(operations);
      show(`${agentsInSource.length} agentes movidos exitosamente`);

      setSourceSkill('');
      setTargetSkill('');
      loadData();
    } catch (e) {
      show(`Error moviendo agentes: ${e.message}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Copiar agentes de un skill a otro
  const handleCopyAgents = async () => {
    if (!sourceSkill || !targetSkill) {
      show('Selecciona skill de origen y destino', 'error');
      return;
    }
    if (sourceSkill === targetSkill) {
      show('El skill de origen y destino no pueden ser el mismo', 'error');
      return;
    }

    try {
      setProcessing(true);

      const agentsInSource = getAgentsInSkill(sourceSkill);

      if (agentsInSource.length === 0) {
        show('No hay agentes en el skill de origen', 'error');
        return;
      }

      const operations = agentsInSource.map(agent => ({
        agent: agent.agent,
        id_skill: targetSkill,
        action: 'assign',
        priority: 1
      }));

      const response = await agents.bulkSkillOperations(operations);
      show(`${agentsInSource.length} agentes copiados exitosamente`);

      setSourceSkill('');
      setTargetSkill('');
      loadData();
    } catch (e) {
      show(`Error copiando agentes: ${e.message}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const toggleAgentSelection = (agentId) => {
    setSelectedAgents(prev =>
      prev.includes(agentId)
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const toggleSkillSelection = (skillId) => {
    setSelectedSkills(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  // Filtrar agentes para Tab 1
  const getFilteredAgentsTab1 = () => {
    let filtered = applyHierarchyFilters(agentsList);

    // Filtrar por skill
    if (filterSkillTab1) {
      filtered = filtered.filter(agent =>
        Array.isArray(agent.skills) &&
        agent.skills.some(s => s.id_skill === filterSkillTab1 && s.status === 1)
      );
    }

    // Filtrar por búsqueda
    if (agentSearchTab1) {
      const search = agentSearchTab1.toLowerCase();
      filtered = filtered.filter(agent =>
        agent.name.toLowerCase().includes(search) ||
        agent.agent.toLowerCase().includes(search)
      );
    }

    return filtered;
  };

  // Filtrar agentes para Tab 2 (en el select de agentes a asignar)
  const getFilteredAgentsTab2 = () => {
    let filtered = applyHierarchyFilters(agentsList);

    if (!agentSearchTab2) return filtered;

    const search = agentSearchTab2.toLowerCase();
    return filtered.filter(agent =>
      agent.name.toLowerCase().includes(search) ||
      agent.agent.toLowerCase().includes(search)
    );
  };

  // Filtrar skills para asignar en Tab 1
  const getFilteredSkillsToAssign = () => {
    if (!skillSearchAssign) return skillsList;

    const search = skillSearchAssign.toLowerCase();
    return skillsList.filter(skill =>
      skill.skillname.toLowerCase().includes(search) ||
      skill.skilldef.toLowerCase().includes(search)
    );
  };

  // Filtrar skills para remover en Tab 1
  const getFilteredSkillsToRemove = () => {
    if (!skillSearchRemove) return skillsList;

    const search = skillSearchRemove.toLowerCase();
    return skillsList.filter(skill =>
      skill.skillname.toLowerCase().includes(search) ||
      skill.skilldef.toLowerCase().includes(search)
    );
  };

  // Seleccionar/Deseleccionar todos los agentes filtrados en Tab 1
  const handleSelectAllAgentsTab1 = () => {
    const filteredAgents = getFilteredAgentsTab1();
    const allSelected = filteredAgents.every(agent => selectedAgents.includes(agent.agent));

    if (allSelected) {
      // Deseleccionar todos los filtrados
      setSelectedAgents(prev => prev.filter(id => !filteredAgents.some(a => a.agent === id)));
    } else {
      // Seleccionar todos los filtrados
      const newSelected = [...new Set([...selectedAgents, ...filteredAgents.map(a => a.agent)])];
      setSelectedAgents(newSelected);
    }
  };

  // Verificar si todos los agentes filtrados están seleccionados
  const areAllFilteredAgentsSelected = () => {
    const filteredAgents = getFilteredAgentsTab1();
    if (filteredAgents.length === 0) return false;
    return filteredAgents.every(agent => selectedAgents.includes(agent.agent));
  };

  // ========== FUNCIONES DRAG & DROP ==========

  // Obtener agentes seleccionados del contexto activo
  const getSelectedAgents = (context) => {
    return selectedAgentsDrag[context] || [];
  };

  // Obtener todos los agentes seleccionados (de todos los contextos)
  const getAllSelectedAgents = () => {
    return Object.values(selectedAgentsDrag).flat();
  };

  // Contar total de agentes seleccionados
  const getSelectedCount = (context) => {
    return getSelectedAgents(context).length;
  };

  const toggleSkill = (skillId) => {
    setExpandedSkills(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const toggleAgentSelectionDrag = (agentId, context, shiftKey = false) => {
    // Cambiar el contexto activo
    setActiveSelectionContext(context);

    if (shiftKey && lastSelectedAgent) {
      // Selección de rango con Shift
      const skillId = context === 'panel' ? null : parseInt(context.replace('skill_', ''));
      const filteredAgents = context === 'panel' ? getFilteredAgentsForDrag() : getAgentsInSkill(skillId);
      const lastIndex = filteredAgents.findIndex(a => a.agent === lastSelectedAgent);
      const currentIndex = filteredAgents.findIndex(a => a.agent === agentId);

      if (lastIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastIndex, currentIndex);
        const end = Math.max(lastIndex, currentIndex);
        const rangeAgents = filteredAgents.slice(start, end + 1).map(a => a.agent);

        // Agregar todos los agentes del rango a la selección del contexto
        setSelectedAgentsDrag(prev => {
          const currentSelection = prev[context] || [];
          const newSelection = [...currentSelection];
          rangeAgents.forEach(id => {
            if (!newSelection.includes(id)) {
              newSelection.push(id);
            }
          });
          return { ...prev, [context]: newSelection };
        });
      }
    } else {
      // Click normal: toggle individual en el contexto específico
      setSelectedAgentsDrag(prev => {
        const currentSelection = prev[context] || [];
        const newSelection = currentSelection.includes(agentId)
          ? currentSelection.filter(id => id !== agentId)
          : [...currentSelection, agentId];
        return { ...prev, [context]: newSelection };
      });
      setLastSelectedAgent(agentId);
    }
  };

  const handleDragStart = (e, agent, context, skillId = null) => {
    if (!e.dataTransfer) return;
    e.stopPropagation();

    // Si el agente arrastrado está en la selección del contexto, arrastrar todos los seleccionados
    const contextSelection = getSelectedAgents(context);
    const agentsToDrag = contextSelection.includes(agent.agent)
      ? agentsList.filter(a => contextSelection.includes(a.agent))
      : [agent];

    const initialCopyMode = e.ctrlKey || e.metaKey;
    console.log('[DragStart] ctrlKey:', e.ctrlKey, 'metaKey:', e.metaKey, 'copyMode:', initialCopyMode);

    setDraggedAgents(agentsToDrag);
    setDraggedFromSkill(skillId ?? null);
    setIsDragging(true);
    setDragModeCopy(initialCopyMode);
    dragModeCopyRef.current = initialCopyMode;

    // Configurar el dataTransfer
    e.dataTransfer.effectAllowed = 'copyMove'; // Permitir ambas acciones
    const transferPayload = {
      agentIds: agentsToDrag.map(a => a.agent),
      sourceSkill: skillId ?? null,
      copy: initialCopyMode
    };
    e.dataTransfer.setData('application/json', JSON.stringify(transferPayload));
    e.dataTransfer.setData('text/plain', JSON.stringify(transferPayload.agentIds));

    // Establecer el efecto inicial basado en el estado de Ctrl/Cmd
    e.dataTransfer.dropEffect = initialCopyMode ? 'copy' : 'move';

    // Crear imagen personalizada del drag con indicador de modo
    if (agentsToDrag.length > 0) {
      const dragImage = document.createElement('div');
      dragImage.style.cssText = `
        position: absolute;
        top: -1000px;
        background: ${initialCopyMode ? '#2196f3' : '#ff9800'};
        color: white;
        padding: 8px 12px;
        border-radius: 4px;
        font-weight: bold;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      `;
      dragImage.textContent = `${initialCopyMode ? '✓ ' : '→ '}${agentsToDrag.length} agente${agentsToDrag.length > 1 ? 's' : ''}`;
      document.body.appendChild(dragImage);
      e.dataTransfer.setDragImage(dragImage, 0, 0);
      setTimeout(() => document.body.removeChild(dragImage), 0);
    }
  };

  const handleDragOver = (e, skillId) => {
    e.preventDefault();
    e.stopPropagation();

    // No permitir arrastrar un skill sobre sí mismo
    if (draggedFromSkill === skillId) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    // Detectar si Ctrl/Cmd está presionado en este momento
    const isCopyMode = e.ctrlKey || e.metaKey;
    e.dataTransfer.dropEffect = isCopyMode ? 'copy' : 'move';

    // Actualizar el ref para que esté disponible en handleDrop
    dragModeCopyRef.current = isCopyMode;

    // Debug log (solo cuando cambia el modo)
    if (isDragging && dragModeCopy !== isCopyMode) {
      console.log('[DragOver] ctrlKey:', e.ctrlKey, 'metaKey:', e.metaKey, 'copyMode:', isCopyMode);
    }

    // Actualizar el estado del modo de copia solo si cambió (evitar re-renders)
    setDragModeCopy(prev => prev !== isCopyMode ? isCopyMode : prev);

    if (dragOverSkill !== skillId) {
      setDragOverSkill(skillId);
    }
  };

  const handleDragLeave = () => {
    setDragOverSkill(null);
  };

  const handleDrop = async (e, targetSkillId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSkill(null);

    // Leer payload del dataTransfer como respaldo (evita perder info si el estado se limpia)
    let dropPayload = null;
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (raw) dropPayload = JSON.parse(raw);
    } catch (err) {
      console.error('Error leyendo dataTransfer', err);
    }

    const effectiveDraggedAgents = (draggedAgents && draggedAgents.length > 0)
      ? draggedAgents
      : dropPayload?.agentIds
        ? agentsList.filter(a => dropPayload.agentIds.includes(a.agent))
        : [];

    if (!effectiveDraggedAgents || effectiveDraggedAgents.length === 0) return;

    // Detectar si Ctrl/Cmd está presionado AL MOMENTO de soltar
    // Prioridad: evento actual > ref > estado > payload
    const isCopy = (e.ctrlKey || e.metaKey) || dragModeCopyRef.current || dragModeCopy || dropPayload?.copy;
    console.log('[Drop] ctrlKey:', e.ctrlKey, 'metaKey:', e.metaKey, 'ref:', dragModeCopyRef.current, 'state:', dragModeCopy, 'final isCopy:', isCopy);
    const sourceSkill = draggedFromSkill ?? dropPayload?.sourceSkill ?? null;
    const hasSourceSkill = sourceSkill !== null && sourceSkill !== undefined;
    const movingBetweenSkills = hasSourceSkill && sourceSkill !== targetSkillId;
    const shouldRemoveFromSource = !isCopy && movingBetweenSkills;

    // Actualización optimista del UI
    const updatedAgents = agentsList.map(agent => {
      if (!effectiveDraggedAgents.some(da => da.agent === agent.agent)) return agent;

      let newSkills = [...(agent.skills || [])];

      // Agregar al skill destino
      const targetIndex = newSkills.findIndex(s => s.id_skill === targetSkillId);
      if (targetIndex === -1) {
        newSkills.push({ id_skill: targetSkillId, status: 1 });
      } else {
        newSkills[targetIndex] = { ...newSkills[targetIndex], status: 1 };
      }

      // Si NO es copia (es mover), quitar del origen
      if (shouldRemoveFromSource) {
        newSkills = newSkills.map(s =>
          s.id_skill === sourceSkill ? { ...s, status: 0 } : s
        );
      }

      return { ...agent, skills: newSkills };
    });

    // Actualizar UI inmediatamente
    setAgentsList(updatedAgents);
    setSelectedAgentsDrag({}); // Limpiar todas las selecciones

    // Preparar operaciones para el servidor
    const operations = [];

    effectiveDraggedAgents.forEach(agent => {
      // Asignar al skill destino
      operations.push({
        agent: agent.agent,
        id_skill: targetSkillId,
        action: 'assign',
        priority: 1
      });

      // Si NO es copia, desasignar del origen
      if (shouldRemoveFromSource) {
        operations.push({
          agent: agent.agent,
          id_skill: sourceSkill,
          action: 'unassign'
        });
      }
    });

    // Enviar al servidor en segundo plano
    (async () => {
      try {
        await agents.bulkSkillOperations(operations);

        const count = effectiveDraggedAgents.length;
        const action = isCopy ? 'copiado' : (shouldRemoveFromSource ? 'movido' : 'asignado');
        show(`${count} agente${count > 1 ? 's' : ''} ${action}${count > 1 ? 's' : ''} exitosamente`);

        // Recargar datos del servidor para sincronizar
        loadData();
      } catch (e) {
        show(`Error: ${e.message}`, 'error');
        // Revertir en caso de error
        loadData();
      }
    })();

    setDraggedAgents([]);
    setDraggedFromSkill(null);
    setIsDragging(false);
    setDragModeCopy(false);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDragModeCopy(false);
    dragModeCopyRef.current = false;
    setDraggedAgents([]);
    setDraggedFromSkill(null);
  };

  // ========== FUNCIONES DE ARRASTRE DE SKILLS ==========
  const handleSkillDragStart = (e, skill) => {
    if (!e.dataTransfer) return;
    e.stopPropagation();

    // Obtener todos los agentes del skill que estamos arrastrando
    const agentsInSkill = getAgentsInSkill(skill.id);

    if (agentsInSkill.length === 0) {
      e.preventDefault();
      show('El skill no tiene agentes para mover', 'warning');
      return;
    }

    const initialCopyMode = e.ctrlKey || e.metaKey;
    console.log('[SkillDragStart] Skill:', skill.skillname, 'Agents:', agentsInSkill.length, 'copyMode:', initialCopyMode);

    setDraggedAgents(agentsInSkill);
    setDraggedFromSkill(skill.id);
    setIsDragging(true);
    setDragModeCopy(initialCopyMode);
    dragModeCopyRef.current = initialCopyMode;

    // Configurar el dataTransfer
    e.dataTransfer.effectAllowed = 'copyMove';
    const transferPayload = {
      agentIds: agentsInSkill.map(a => a.agent),
      sourceSkill: skill.id,
      copy: initialCopyMode,
      isSkillDrag: true // Flag para identificar que es un arrastre de skill
    };
    e.dataTransfer.setData('application/json', JSON.stringify(transferPayload));
    e.dataTransfer.setData('text/plain', JSON.stringify(transferPayload.agentIds));

    // Establecer el efecto inicial
    e.dataTransfer.dropEffect = initialCopyMode ? 'copy' : 'move';

    // Crear imagen personalizada del drag
    const dragImage = document.createElement('div');
    dragImage.style.cssText = `
      position: absolute;
      top: -1000px;
      background: ${initialCopyMode ? '#2196f3' : '#ff9800'};
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-weight: bold;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `;
    dragImage.textContent = `${initialCopyMode ? '✓ ' : '→ '}${skill.skillname} (${agentsInSkill.length} agente${agentsInSkill.length > 1 ? 's' : ''})`;
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleRemoveAgentFromSkill = async (agent, skillId) => {
    try {
      setProcessing(true);

      const operations = [{
        agent: agent.agent,
        id_skill: skillId,
        action: 'unassign'
      }];

      await agents.bulkSkillOperations(operations);
      show(`${agent.name} desasignado del skill exitosamente`);
      loadData();
    } catch (e) {
      show(`Error: ${e.message}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  // ========== FUNCIONES COPIAR/CORTAR/PEGAR/ELIMINAR ==========

  const handleCopy = (context) => {
    const contextSelection = getSelectedAgents(context);
    if (contextSelection.length === 0) {
      show('Selecciona al menos un agente para copiar', 'warning');
      return;
    }

    const agentsToCopy = agentsList.filter(a => contextSelection.includes(a.agent));
    const skillId = context.startsWith('skill_') ? parseInt(context.replace('skill_', '')) : null;

    // Actualización inmediata del UI
    setClipboard({ agents: agentsToCopy, mode: 'copy', sourceSkill: skillId });
    show(`${agentsToCopy.length} agente${agentsToCopy.length > 1 ? 's' : ''} copiado${agentsToCopy.length > 1 ? 's' : ''} al portapapeles`, 'info');
  };

  const handleCut = (context) => {
    const contextSelection = getSelectedAgents(context);
    if (contextSelection.length === 0) {
      show('Selecciona al menos un agente para cortar', 'warning');
      return;
    }

    if (!context || context === 'panel') {
      show('Solo puedes cortar agentes desde un skill específico', 'warning');
      return;
    }

    const agentsToCut = agentsList.filter(a => contextSelection.includes(a.agent));
    const skillId = parseInt(context.replace('skill_', ''));

    // Actualización inmediata del UI
    setClipboard({ agents: agentsToCut, mode: 'cut', sourceSkill: skillId });
    show(`${agentsToCut.length} agente${agentsToCut.length > 1 ? 's' : ''} cortado${agentsToCut.length > 1 ? 's' : ''} al portapapeles`, 'info');
  };

  const handlePaste = async (targetSkillId) => {
    if (!clipboard.agents || clipboard.agents.length === 0) {
      show('El portapapeles está vacío', 'warning');
      return;
    }

    const isCut = clipboard.mode === 'cut';
    const sourceSkillId = clipboard.sourceSkill;

    // Actualización optimista del UI
    const updatedAgents = agentsList.map(agent => {
      const agentInClipboard = clipboard.agents.find(a => a.agent === agent.agent);
      if (!agentInClipboard) return agent;

      let newSkills = [...(agent.skills || [])];

      // Agregar al skill destino
      if (!newSkills.some(s => s.id_skill === targetSkillId)) {
        newSkills.push({ id_skill: targetSkillId, status: 1 });
      }

      // Si es cortar, quitar del origen
      if (isCut && sourceSkillId && sourceSkillId !== targetSkillId) {
        newSkills = newSkills.map(s =>
          s.id_skill === sourceSkillId ? { ...s, status: 0 } : s
        );
      }

      return { ...agent, skills: newSkills };
    });

    // Actualizar UI inmediatamente
    setAgentsList(updatedAgents);
    setSelectedAgentsDrag({});

    // Limpiar portapapeles si fue cortar
    if (isCut) {
      setClipboard({ agents: [], mode: null, sourceSkill: null });
    }

    // Preparar operaciones para el servidor
    const operations = [];

    clipboard.agents.forEach(agent => {
      // Asignar al skill destino
      operations.push({
        agent: agent.agent,
        id_skill: targetSkillId,
        action: 'assign',
        priority: 1
      });

      // Si es cortar, desasignar del origen
      if (isCut && sourceSkillId && sourceSkillId !== targetSkillId) {
        operations.push({
          agent: agent.agent,
          id_skill: sourceSkillId,
          action: 'unassign'
        });
      }
    });

    // Enviar al servidor en segundo plano
    (async () => {
      try {
        await agents.bulkSkillOperations(operations);

        const count = clipboard.agents.length;
        const action = isCut ? 'movidos' : 'copiados';
        show(`${count} agente${count > 1 ? 's' : ''} ${action} exitosamente`, 'success');

        // Recargar datos del servidor para sincronizar
        loadData();
      } catch (e) {
        show(`Error: ${e.message}`, 'error');
        // Revertir en caso de error
        loadData();
      }
    })();
  };

  const handleDeleteSelected = async (context) => {
    const contextSelection = getSelectedAgents(context);
    if (contextSelection.length === 0) {
      show('Selecciona al menos un agente para eliminar', 'warning');
      return;
    }

    const skillId = context.startsWith('skill_') ? parseInt(context.replace('skill_', '')) : null;
    const operations = [];

    if (skillId) {
      // Eliminar solo del skill específico
      contextSelection.forEach(agentId => {
        operations.push({
          agent: agentId,
          id_skill: skillId,
          action: 'unassign'
        });
      });
    } else {
      // Eliminar de todos los skills (desasignar completamente)
      contextSelection.forEach(agentId => {
        const agent = agentsList.find(a => a.agent === agentId);
        if (agent && agent.skills) {
          agent.skills.forEach(skill => {
            if (skill.status === 1) {
              operations.push({
                agent: agentId,
                id_skill: skill.id_skill,
                action: 'unassign'
              });
            }
          });
        }
      });
    }

    if (operations.length === 0) {
      show('No hay agentes para desasignar', 'warning');
      return;
    }

    // Actualización optimista del UI
    const updatedAgents = agentsList.map(agent => {
      if (!contextSelection.includes(agent.agent)) return agent;

      let newSkills = [...(agent.skills || [])];

      if (skillId) {
        // Desasignar solo del skill específico
        newSkills = newSkills.map(s =>
          s.id_skill === skillId ? { ...s, status: 0 } : s
        );
      } else {
        // Desasignar de todos los skills
        newSkills = newSkills.map(s => ({ ...s, status: 0 }));
      }

      return { ...agent, skills: newSkills };
    });

    // Actualizar UI inmediatamente
    setAgentsList(updatedAgents);
    setSelectedAgentsDrag({});

    const count = contextSelection.length;
    show(`${count} agente${count > 1 ? 's' : ''} desasignado${count > 1 ? 's' : ''} exitosamente`, 'success');

    // Enviar al servidor en segundo plano
    (async () => {
      try {
        await agents.bulkSkillOperations(operations);

        // Recargar datos del servidor para sincronizar
        loadData();
      } catch (e) {
        show(`Error: ${e.message}`, 'error');
        // Revertir en caso de error
        loadData();
      }
    })();
  };

  const handleContextMenu = (e, context) => {
    e.preventDefault();
    const skillId = context?.startsWith('skill_') ? parseInt(context.replace('skill_', '')) : null;
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      skillId,
      context
    });
    setActiveSelectionContext(context);
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, skillId: null });
  };

  // Detectar atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Solo procesar atajos si estamos en la pestaña de drag & drop
      if (tabValue !== 3) return;

      // Solo procesar si hay un contexto activo con selección
      if (!activeSelectionContext) return;

      const contextSelection = getSelectedAgents(activeSelectionContext);
      if (contextSelection.length === 0) return;

      // Ctrl+C o Cmd+C: Copiar
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        handleCopy(activeSelectionContext);
      }

      // Ctrl+X o Cmd+X: Cortar
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        handleCut(activeSelectionContext);
      }

      // Delete (Suprimir): Eliminar
      // Solo Delete, no Backspace para evitar conflictos con campos de texto
      if (e.key === 'Delete') {
        e.preventDefault();
        handleDeleteSelected(activeSelectionContext);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [tabValue, selectedAgentsDrag, agentsList, clipboard, activeSelectionContext]);

  // Cerrar menú contextual al hacer click fuera
  useEffect(() => {
    const handleClick = () => {
      if (contextMenu.visible) {
        closeContextMenu();
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [contextMenu.visible]);

  // Obtener agentes sin asignar a ningún skill
  const getUnassignedAgents = () => {
    return applyHierarchyFilters(agentsList).filter(agent =>
      !Array.isArray(agent.skills) ||
      agent.skills.length === 0 ||
      !agent.skills.some(s => s.status === 1)
    );
  };

  // Filtrar agentes para panel izquierdo según skill seleccionado
  const getFilteredAgentsForDrag = () => {
    let filtered = applyHierarchyFilters(agentsList);

    // Filtrar por skill
    if (filterSkillDrag === 'unassigned') {
      filtered = getUnassignedAgents();
    } else if (filterSkillDrag !== 'all') {
      filtered = getAgentsInSkill(filterSkillDrag);
    }

    // Filtrar por búsqueda - soporta múltiples agentes separados por comas
    if (searchDragView) {
      const search = searchDragView.toLowerCase().trim();

      // Si contiene comas, buscar por múltiples agentes
      if (search.includes(',')) {
        const searchTerms = search.split(',').map(term => term.trim()).filter(term => term.length > 0);
        filtered = filtered.filter(agent =>
          searchTerms.some(term =>
            agent.name.toLowerCase().includes(term) ||
            agent.agent.toLowerCase().includes(term)
          )
        );
      } else {
        // Búsqueda simple
        filtered = filtered.filter(agent =>
          agent.name.toLowerCase().includes(search) ||
          agent.agent.toLowerCase().includes(search)
        );
      }
    }

    return filtered;
  };

  // Filtrar skills y agentes para vista drag
  const getFilteredSkillsForDrag = () => {
    let filtered = skillsList;

    // Filtrar por campaña
    if (filterCampaignDrag !== 'all') {
      if (filterCampaignDrag === 'sin_campana') {
        filtered = filtered.filter(skill => !skill.idcampaign);
      } else {
        filtered = filtered.filter(skill => skill.idcampaign === filterCampaignDrag);
      }
    }

    // NO filtrar skills por búsqueda de agentes - solo filtrar el panel de agentes
    // La búsqueda ahora solo afecta al panel izquierdo de agentes

    return filtered;
  };

  // Agrupar skills por campaña
  const getSkillsGroupedByCampaign = () => {
    const filtered = getFilteredSkillsForDrag();
    const grouped = {};

    filtered.forEach(skill => {
      const campaignKey = skill.idcampaign || 'sin_campana';
      const campaignName = skill.campaign_name || 'Sin Campaña';

      if (!grouped[campaignKey]) {
        grouped[campaignKey] = {
          id: campaignKey,
          name: campaignName,
          skills: []
        };
      }

      grouped[campaignKey].skills.push(skill);
    });

    return Object.values(grouped);
  };

  // Toggle campaña expandida
  const toggleCampaign = (campaignId) => {
    setExpandedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  // Obtener lista única de campañas
  const getCampaignsList = () => {
    const campaigns = new Map();
    skillsList.forEach(skill => {
      const campaignKey = skill.idcampaign || 'sin_campana';
      const campaignName = skill.campaign_name || 'Sin Campaña';
      if (!campaigns.has(campaignKey)) {
        campaigns.set(campaignKey, { id: campaignKey, name: campaignName });
      }
    });
    return Array.from(campaigns.values());
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" component="h1">
                  Asignación Masiva de Skills
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadData}
                  disabled={loading || processing}
                >
                  Actualizar
                </Button>
              </Box>

              {loading && <LinearProgress sx={{ mb: 2 }} />}

              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab label="Vista Visual" icon={<ViewModuleIcon />} />
                <Tab label="Asignar Skills a Agentes" icon={<AssignmentIcon />} />
                <Tab label="Asignar Agentes a Skills" icon={<AssignmentIcon />} />
                <Tab label="Mover / Copiar Agentes" icon={<SwapIcon />} />
              </Tabs>

              {/* TAB 2: Asignar Skills a Agentes */}
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel>Filtrar por skill</InputLabel>
                    <Select
                      value={filterSkillTab1}
                      onChange={(e) => setFilterSkillTab1(e.target.value)}
                      label="Filtrar por skill"
                    >
                      <MenuItem value="">Todos los agentes</MenuItem>
                      {skillsList.map(skill => (
                        <MenuItem key={skill.id} value={skill.id}>
                          {skill.skillname} ({getAgentsInSkill(skill.id).length})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 200 }} size="small">
                    <InputLabel>Supervisor</InputLabel>
                    <Select
                      value={filterSupervisor}
                      label="Supervisor"
                      onChange={(e) => setFilterSupervisor(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Todos</em>
                      </MenuItem>
                      {supervisorOptions.map((sup) => (
                        <MenuItem key={sup.id} value={sup.id}>
                          {sup.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 200 }} size="small">
                    <InputLabel>Gerente</InputLabel>
                    <Select
                      value={filterManager}
                      label="Gerente"
                      onChange={(e) => setFilterManager(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Todos</em>
                      </MenuItem>
                      {managerOptions.map((mgr) => (
                        <MenuItem key={mgr.id} value={mgr.id}>
                          {mgr.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={5}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="h6">
                            Seleccionar Agentes ({selectedAgents.length})
                          </Typography>
                          <Button
                            size="small"
                            onClick={handleSelectAllAgentsTab1}
                            disabled={getFilteredAgentsTab1().length === 0}
                          >
                            {areAllFilteredAgentsSelected() ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
                          </Button>
                        </Box>

                        <TextField
                          fullWidth
                          size="small"
                          label="Buscar agente"
                          placeholder="Nombre o ID..."
                          value={agentSearchTab1}
                          onChange={(e) => setAgentSearchTab1(e.target.value)}
                          sx={{ mb: 1 }}
                        />

                        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                          <List dense>
                            {getFilteredAgentsTab1().length === 0 ? (
                              <ListItem>
                                <ListItemText
                                  primary="No se encontraron agentes"
                                  secondary="Intenta cambiar los filtros de búsqueda"
                                  sx={{ textAlign: 'center', color: 'text.secondary' }}
                                />
                              </ListItem>
                            ) : (
                              getFilteredAgentsTab1().map(agent => (
                                <ListItemButton
                                  key={agent.agent}
                                  onClick={() => toggleAgentSelection(agent.agent)}
                                  selected={selectedAgents.includes(agent.agent)}
                                >
                                  <Checkbox
                                    edge="start"
                                    checked={selectedAgents.includes(agent.agent)}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                  <ListItemText
                                    primary={`${agent.name} (${agent.agent})`}
                                    secondary={`${getSkillsForAgent(agent.agent).length} skills`}
                                  />
                                </ListItemButton>
                              ))
                            )}
                          </List>
                        </Paper>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Operaciones
                        </Typography>

                        <Box mb={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            Asignar Skills
                          </Typography>
                          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel>Seleccionar Skills</InputLabel>
                            <Select
                              multiple
                              value={skillsToAssign}
                              onChange={(e) => setSkillsToAssign(e.target.value)}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map(id => {
                                    const skill = skillsList.find(s => s.id === id);
                                    return <Chip key={id} label={skill?.skillname || id} size="small" />;
                                  })}
                                </Box>
                              )}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 400
                                  }
                                },
                                autoFocus: false
                              }}
                            >
                              <MenuItem
                                disableRipple
                                onKeyDown={(e) => e.stopPropagation()}
                                sx={{
                                  position: 'sticky',
                                  top: 0,
                                  backgroundColor: 'background.paper',
                                  zIndex: 1,
                                  '&:hover': { backgroundColor: 'background.paper' }
                                }}
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Buscar skill..."
                                  value={skillSearchAssign}
                                  onChange={(e) => setSkillSearchAssign(e.target.value)}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                              </MenuItem>
                              {getFilteredSkillsToAssign().map(skill => (
                                <MenuItem key={skill.id} value={skill.id}>
                                  <Checkbox checked={skillsToAssign.includes(skill.id)} />
                                  <ListItemText primary={`${skill.skillname} (${skill.skilldef})`} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {canAction('assign_skills_to_agents') && (
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAssignSkillsToAgents}
                            disabled={processing || selectedAgents.length === 0 || skillsToAssign.length === 0}
                          >
                            Asignar {skillsToAssign.length} skill(s) a {selectedAgents.length} agente(s)
                          </Button>
                          )}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Quitar Skills
                          </Typography>
                          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Seleccionar Skills a Quitar</InputLabel>
                            <Select
                              multiple
                              value={skillsToRemove}
                              onChange={(e) => setSkillsToRemove(e.target.value)}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map(id => {
                                    const skill = skillsList.find(s => s.id === id);
                                    return <Chip key={id} label={skill?.skillname || id} size="small" />;
                                  })}
                                </Box>
                              )}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 400
                                  }
                                },
                                autoFocus: false
                              }}
                            >
                              <MenuItem
                                disableRipple
                                onKeyDown={(e) => e.stopPropagation()}
                                sx={{
                                  position: 'sticky',
                                  top: 0,
                                  backgroundColor: 'background.paper',
                                  zIndex: 1,
                                  '&:hover': { backgroundColor: 'background.paper' }
                                }}
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Buscar skill..."
                                  value={skillSearchRemove}
                                  onChange={(e) => setSkillSearchRemove(e.target.value)}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                              </MenuItem>
                              {getFilteredSkillsToRemove().map(skill => (
                                <MenuItem key={skill.id} value={skill.id}>
                                  <Checkbox checked={skillsToRemove.includes(skill.id)} />
                                  <ListItemText primary={`${skill.skillname} (${skill.skilldef})`} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {canAction('remove_skills_from_agents') && (
                          <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            startIcon={<RemoveIcon />}
                            onClick={handleRemoveSkillsFromAgents}
                            disabled={processing || selectedAgents.length === 0 || skillsToRemove.length === 0}
                          >
                            Quitar {skillsToRemove.length} skill(s) de {selectedAgents.length} agente(s)
                          </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* TAB 3: Asignar Agentes a Skills */}
              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={5}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Seleccionar Skills ({selectedSkills.length})
                        </Typography>
                        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                          <List dense>
                            {skillsList.map(skill => (
                              <ListItemButton
                                key={skill.id}
                                onClick={() => toggleSkillSelection(skill.id)}
                                selected={selectedSkills.includes(skill.id)}
                              >
                                <Checkbox
                                  edge="start"
                                  checked={selectedSkills.includes(skill.id)}
                                  tabIndex={-1}
                                  disableRipple
                                />
                                <ListItemText
                                  primary={`${skill.skillname} (${skill.skilldef})`}
                                  secondary={`${getAgentsInSkill(skill.id).length} agentes`}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      </CardContent>
                    </Card>
                  </Grid>

                <Grid item xs={12} md={7}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Asignar Agentes a Skills
                      </Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                          <InputLabel>Seleccionar Agentes</InputLabel>
                          <Select
                            multiple
                            value={agentsToAssign}
                            onChange={(e) => setAgentsToAssign(e.target.value)}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map(id => {
                                  const agent = agentsList.find(a => a.agent === id);
                                  return <Chip key={id} label={agent?.name || id} size="small" />;
                                })}
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 400
                                }
                              },
                              autoFocus: false
                            }}
                          >
                            <MenuItem
                              disableRipple
                              onKeyDown={(e) => e.stopPropagation()}
                              sx={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'background.paper',
                                zIndex: 1,
                                '&:hover': { backgroundColor: 'background.paper' }
                              }}
                            >
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Buscar agente..."
                                value={agentSearchTab2}
                                onChange={(e) => setAgentSearchTab2(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                            </MenuItem>
                            <MenuItem disabled sx={{ opacity: 1, fontWeight: 600 }}>
                              Supervisión
                            </MenuItem>
                            <MenuItem disableRipple onKeyDown={(e) => e.stopPropagation()} sx={{ '&:hover': { backgroundColor: 'background.paper' } }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Supervisor</InputLabel>
                                <Select
                                  value={filterSupervisor}
                                  label="Supervisor"
                                  onChange={(e) => setFilterSupervisor(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onClose={(e) => e.stopPropagation()}
                                >
                                  <MenuItem value="">
                                    <em>Todos</em>
                                  </MenuItem>
                                  {supervisorOptions.map((sup) => (
                                    <MenuItem key={sup.id} value={sup.id}>
                                      {sup.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </MenuItem>
                            <MenuItem disableRipple onKeyDown={(e) => e.stopPropagation()} sx={{ '&:hover': { backgroundColor: 'background.paper' } }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Gerente</InputLabel>
                                <Select
                                  value={filterManager}
                                  label="Gerente"
                                  onChange={(e) => setFilterManager(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onClose={(e) => e.stopPropagation()}
                                >
                                  <MenuItem value="">
                                    <em>Todos</em>
                                  </MenuItem>
                                  {managerOptions.map((mgr) => (
                                    <MenuItem key={mgr.id} value={mgr.id}>
                                      {mgr.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </MenuItem>
                            <MenuItem disableRipple sx={{ opacity: 1, fontWeight: 600, cursor: 'default', '&:hover': { backgroundColor: 'background.paper' } }}>
                              Supervisión
                            </MenuItem>
                            <MenuItem disableRipple onKeyDown={(e) => e.stopPropagation()} sx={{ '&:hover': { backgroundColor: 'background.paper' } }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Supervisor</InputLabel>
                                <Select
                                  value={filterSupervisor}
                                  label="Supervisor"
                                  onChange={(e) => setFilterSupervisor(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onClose={(e) => e.stopPropagation()}
                                >
                                  <MenuItem value="">
                                    <em>Todos</em>
                                  </MenuItem>
                                  {supervisorOptions.map((sup) => (
                                    <MenuItem key={sup.id} value={sup.id}>
                                      {sup.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </MenuItem>
                            <MenuItem disableRipple onKeyDown={(e) => e.stopPropagation()} sx={{ '&:hover': { backgroundColor: 'background.paper' } }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Gerente</InputLabel>
                                <Select
                                  value={filterManager}
                                  label="Gerente"
                                  onChange={(e) => setFilterManager(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onClose={(e) => e.stopPropagation()}
                                >
                                  <MenuItem value="">
                                    <em>Todos</em>
                                  </MenuItem>
                                  {managerOptions.map((mgr) => (
                                    <MenuItem key={mgr.id} value={mgr.id}>
                                      {mgr.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </MenuItem>
                            {getFilteredAgentsTab2().map(agent => (
                              <MenuItem key={agent.agent} value={agent.agent}>
                                <Checkbox checked={agentsToAssign.includes(agent.agent)} />
                                <ListItemText primary={`${agent.name} (${agent.agent})`} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {canAction('assign_skills_to_agents') && (
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAssignAgentsToSkills}
                          disabled={processing || selectedSkills.length === 0 || agentsToAssign.length === 0}
                        >
                          Asignar {agentsToAssign.length} agente(s) a {selectedSkills.length} skill(s)
                        </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* TAB 4: Mover / Copiar Agentes */}
              <TabPanel value={tabValue} index={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Mover Agentes
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Mueve todos los agentes de un skill a otro (los quita del origen)
                        </Typography>

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Skill de Origen</InputLabel>
                          <Select
                            value={sourceSkill}
                            onChange={(e) => setSourceSkill(e.target.value)}
                            label="Skill de Origen"
                          >
                            {skillsList.map(skill => (
                              <MenuItem key={skill.id} value={skill.id}>
                                {skill.skillname} ({getAgentsInSkill(skill.id).length} agentes)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Skill de Destino</InputLabel>
                          <Select
                            value={targetSkill}
                            onChange={(e) => setTargetSkill(e.target.value)}
                            label="Skill de Destino"
                          >
                            {skillsList.filter(s => s.id !== sourceSkill).map(skill => (
                              <MenuItem key={skill.id} value={skill.id}>
                                {skill.skillname} ({getAgentsInSkill(skill.id).length} agentes)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {canAction('move_agents_skills') && (
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<SwapIcon />}
                          onClick={handleMoveAgents}
                          disabled={processing || !sourceSkill || !targetSkill}
                        >
                          Mover Agentes
                        </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Copiar Agentes
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Copia todos los agentes de un skill a otro (mantiene el origen)
                        </Typography>

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Skill de Origen</InputLabel>
                          <Select
                            value={sourceSkill}
                            onChange={(e) => setSourceSkill(e.target.value)}
                            label="Skill de Origen"
                          >
                            {skillsList.map(skill => (
                              <MenuItem key={skill.id} value={skill.id}>
                                {skill.skillname} ({getAgentsInSkill(skill.id).length} agentes)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Skill de Destino</InputLabel>
                          <Select
                            value={targetSkill}
                            onChange={(e) => setTargetSkill(e.target.value)}
                            label="Skill de Destino"
                          >
                            {skillsList.filter(s => s.id !== sourceSkill).map(skill => (
                              <MenuItem key={skill.id} value={skill.id}>
                                {skill.skillname} ({getAgentsInSkill(skill.id).length} agentes)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {canAction('copy_agents_skills') && (
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<CopyIcon />}
                          onClick={handleCopyAgents}
                          disabled={processing || !sourceSkill || !targetSkill}
                        >
                          Copiar Agentes
                        </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* TAB 1: Vista Visual */}
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ mb: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Interacción:</strong> Arrastra agentes hacia los skills. Sin Ctrl/Cmd se mueve (se quita del origen); con Ctrl/Cmd se copia (se mantiene en el origen).
                    <br />
                    <strong>Selección:</strong> Click para seleccionar; Shift + Click para rangos; click derecho para menú contextual.
                    <br />
                    <strong>Atajos:</strong> Ctrl+C copiar, Ctrl+X cortar, Ctrl+V pegar, Delete eliminar.
                  </Alert>

                  {/* Indicador de arrastre activo - DESHABILITADO TEMPORALMENTE PARA DEBUG
                  {isDragging && draggedAgents.length > 0 && (
                    <Alert severity={dragModeCopy ? 'info' : 'warning'} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {dragModeCopy ? <CopyIcon /> : <SwapIcon />}
                      <strong>
                        Arrastrando {draggedAgents.length} agente{draggedAgents.length > 1 ? 's' : ''} -
                        Modo: {dragModeCopy ? 'COPIAR (mantiene en origen)' : 'MOVER (quita del origen) - Presiona Ctrl/Cmd para copiar'}
                      </strong>
                    </Alert>
                  )}
                  */}

                  {/* Indicador de portapapeles */}
                  {clipboard.agents && clipboard.agents.length > 0 && (
                    <Alert severity="success" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {clipboard.mode === 'copy' ? <CopyIcon /> : <CutIcon />}
                      <strong>
                        {clipboard.agents.length} agente{clipboard.agents.length > 1 ? 's' : ''}{' '}
                        {clipboard.mode === 'copy' ? 'copiado' : 'cortado'}{clipboard.agents.length > 1 ? 's' : ''}
                      </strong>
                      - Haz click derecho en un skill y selecciona "Pegar" para asignarlos.
                      <Button
                        size="small"
                        onClick={() => setClipboard({ agents: [], mode: null, sourceSkill: null })}
                        sx={{ ml: 'auto' }}
                      >
                        Cancelar
                      </Button>
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar agentes (ej: 1234 o 1234,5678,9101)..."
                        value={searchDragView}
                        onChange={(e) => setSearchDragView(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Filtrar por Skill</InputLabel>
                        <Select
                          value={filterSkillDrag}
                          onChange={(e) => setFilterSkillDrag(e.target.value)}
                          label="Filtrar por Skill"
                        >
                          <MenuItem value="all">Todos los agentes</MenuItem>
                          <MenuItem value="unassigned">Sin asignar</MenuItem>
                          <Divider />
                          {skillsList.map(skill => (
                            <MenuItem key={skill.id} value={skill.id}>
                              {skill.skillname} ({getAgentsInSkill(skill.id).length})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Filtrar por Campaña</InputLabel>
                        <Select
                          value={filterCampaignDrag}
                          onChange={(e) => setFilterCampaignDrag(e.target.value)}
                          label="Filtrar por Campaña"
                        >
                          <MenuItem value="all">Todas las campañas</MenuItem>
                          {getCampaignsList().map(campaign => (
                            <MenuItem key={campaign.id} value={campaign.id}>
                              {campaign.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Supervisor</InputLabel>
                        <Select
                          value={filterSupervisor}
                          onChange={(e) => setFilterSupervisor(e.target.value)}
                          label="Supervisor"
                        >
                          <MenuItem value="">
                            <em>Todos</em>
                          </MenuItem>
                          {supervisorOptions.map((sup) => (
                            <MenuItem key={sup.id} value={sup.id}>
                              {sup.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Gerente</InputLabel>
                        <Select
                          value={filterManager}
                          onChange={(e) => setFilterManager(e.target.value)}
                          label="Gerente"
                        >
                          <MenuItem value="">
                            <em>Todos</em>
                          </MenuItem>
                          {managerOptions.map((mgr) => (
                            <MenuItem key={mgr.id} value={mgr.id}>
                              {mgr.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <ButtonGroup fullWidth size="small" variant="outlined">
                        <Button
                          onClick={() => setSkillViewMode('grid')}
                          variant={skillViewMode === 'grid' ? 'contained' : 'outlined'}
                          title="Vista Grid"
                        >
                          <ViewModuleIcon fontSize="small" />
                        </Button>
                        <Button
                          onClick={() => setSkillViewMode('tree')}
                          variant={skillViewMode === 'tree' ? 'contained' : 'outlined'}
                          title="Vista Árbol"
                        >
                          <AccountTreeIcon fontSize="small" />
                        </Button>
                        <Button
                          onClick={() => setSkillViewMode('kanban')}
                          variant={skillViewMode === 'kanban' ? 'contained' : 'outlined'}
                          title="Vista Kanban"
                        >
                          <ViewKanbanIcon fontSize="small" />
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>

                <Grid container spacing={2}>
                  {/* Panel de agentes con filtro */}
                  <Grid item xs={12} md={3}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '70vh',
                        overflow: 'auto',
                        bgcolor: '#f5f5f5',
                        border: '2px solid #667eea'
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon /> Agentes ({getFilteredAgentsForDrag().length})
                          </Typography>
                          {getSelectedAgents('panel').length > 0 && (
                            <Chip
                              label={`${getSelectedAgents('panel').length} seleccionados`}
                              size="small"
                              color="primary"
                              onDelete={() => setSelectedAgentsDrag(prev => ({ ...prev, panel: [] }))}
                            />
                          )}
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {getFilteredAgentsForDrag().map(agent => {
                          const isSelected = getSelectedAgents('panel').includes(agent.agent);
                          return (
                            <Box
                              key={agent.agent}
                              draggable={true}
                              onDragStart={(e) => handleDragStart(e, agent, 'panel', null)}
                              onDragEnd={handleDragEnd}
                              onClick={(e) => toggleAgentSelectionDrag(agent.agent, 'panel', e.shiftKey)}
                              onContextMenu={(e) => handleContextMenu(e, 'panel')}
                              sx={{
                                position: 'relative',
                                p: 1,
                                pl: 1.5,
                                mb: 1,
                                bgcolor: isSelected ? 'rgba(33, 150, 243, 0.08)' : 'white',
                                borderLeft: isSelected ? '4px solid #2196f3' : '4px solid transparent',
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'rgba(33, 150, 243, 0.12)',
                                  borderLeftColor: '#2196f3',
                                  boxShadow: 1
                                },
                                '&:active': {
                                  cursor: 'grabbing'
                                }
                              }}
                            >
                              <PersonIcon
                                fontSize="small"
                                sx={{ color: isSelected ? '#2196f3' : 'action.active' }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{ fontWeight: isSelected ? 500 : 400 }}
                                >
                                  {agent.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {agent.agent}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}

                        {getFilteredAgentsForDrag().length === 0 && (
                          <Typography variant="body2" color="text.secondary" textAlign="center">
                            No se encontraron agentes
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Panel de skills */}
                  <Grid item xs={12} md={9}>
                    <Card variant="outlined" sx={{ height: '70vh', overflow: 'auto' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Skills
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {/* Vista Grid (Actual) */}
                        {skillViewMode === 'grid' && (
                          <Grid container spacing={2}>
                          {getFilteredSkillsForDrag().map(skill => {
                            const isExpanded = expandedSkills.includes(skill.id);
                            const agentsInSkill = getAgentsInSkill(skill.id);
                            const isDragOver = dragOverSkill === skill.id;

                            return (
                              <Grid item xs={12} sm={6} md={4} key={skill.id}>
                                <Card
                                  variant="outlined"
                                  onDragOver={(e) => handleDragOver(e, skill.id)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, skill.id)}
                                  onContextMenu={(e) => handleContextMenu(e, `skill_${skill.id}`)}
                                  sx={{
                                    bgcolor: isDragOver ? '#e3f2fd' : 'white',
                                    border: isDragOver ? '2px solid #2196f3' : '1px solid #ddd',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      boxShadow: 2
                                    }
                                  }}
                                >
                                  <CardContent sx={{ p: 2 }}>
                                    {/* Header del skill */}
                                    <Box
                                      draggable={true}
                                      onDragStart={(e) => handleSkillDragStart(e, skill)}
                                      onDragEnd={handleDragEnd}
                                      onClick={() => toggleSkill(skill.id)}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'grab',
                                        mb: isExpanded ? 2 : 0,
                                        '&:hover': {
                                          color: 'primary.main'
                                        },
                                        '&:active': {
                                          cursor: 'grabbing'
                                        }
                                      }}
                                    >
                                      <SkillIcon color={isExpanded ? 'primary' : 'action'} />
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" noWrap fontWeight="bold">
                                          {skill.skillname}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                          {agentsInSkill.length} agente{agentsInSkill.length !== 1 ? 's' : ''}
                                        </Typography>
                                      </Box>
                                    </Box>

                                    {/* Contenido del skill (agentes) */}
                                    {isExpanded && (
                                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                        {agentsInSkill.length === 0 ? (
                                          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                                            Sin agentes
                                          </Typography>
                                        ) : (
                                          agentsInSkill.map(agent => {
                                            const skillContext = `skill_${skill.id}`;
                                            const isSelected = getSelectedAgents(skillContext).includes(agent.agent);
                                            return (
                                              <Box
                                                key={agent.agent}
                                                draggable={true}
                                                onDragStart={(e) => handleDragStart(e, agent, skillContext, skill.id)}
                                                onDragEnd={handleDragEnd}
                                                onClick={(e) => {
                                                  if (!e.target.closest('.delete-icon')) {
                                                    toggleAgentSelectionDrag(agent.agent, skillContext, e.shiftKey);
                                                  }
                                                }}
                                                sx={{
                                                  position: 'relative',
                                                  p: 1,
                                                  pl: 1.5,
                                                  mb: 1,
                                                  bgcolor: isSelected ? 'rgba(33, 150, 243, 0.08)' : '#fafafa',
                                                  borderLeft: isSelected ? '4px solid #2196f3' : '4px solid transparent',
                                                  border: '1px solid #e0e0e0',
                                                  borderRadius: 1,
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  transition: 'all 0.2s ease',
                                                  '&:hover': {
                                                    bgcolor: 'rgba(33, 150, 243, 0.12)',
                                                    borderLeftColor: '#2196f3',
                                                    '& .delete-icon': {
                                                      opacity: 1
                                                    }
                                                  },
                                                  '&:active': {
                                                    cursor: 'grabbing'
                                                  }
                                                }}
                                              >
                                                <PersonIcon
                                                  fontSize="small"
                                                  sx={{ color: isSelected ? '#2196f3' : 'action.active' }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                  <Typography
                                                    variant="body2"
                                                    noWrap
                                                    fontSize="0.875rem"
                                                    sx={{ fontWeight: isSelected ? 500 : 400 }}
                                                  >
                                                    {agent.name}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary" noWrap fontSize="0.75rem">
                                                    {agent.agent}
                                                  </Typography>
                                                </Box>
                                                {canAction('remove_skills_from_agents') && (
                                                <IconButton
                                                  className="delete-icon"
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveAgentFromSkill(agent, skill.id);
                                                  }}
                                                  disabled={processing}
                                                  sx={{
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s',
                                                    '&:hover': {
                                                      color: 'error.main'
                                                    }
                                                  }}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                                )}
                                              </Box>
                                            );
                                          })
                                        )}
                                      </Box>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                          </Grid>
                        )}

                        {/* Vista Árbol */}
                        {skillViewMode === 'tree' && (
                          <Box>
                            {getSkillsGroupedByCampaign().map(campaign => {
                              const isCampaignExpanded = expandedCampaigns.includes(campaign.id);

                              return (
                                <Box key={campaign.id} sx={{ mb: 2 }}>
                                  {/* Header de Campaña */}
                                  <Box
                                    onClick={() => toggleCampaign(campaign.id)}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      p: 1.5,
                                      bgcolor: '#f5f5f5',
                                      borderRadius: 1,
                                      cursor: 'pointer',
                                      border: '1px solid #ddd',
                                      '&:hover': {
                                        bgcolor: '#e0e0e0'
                                      }
                                    }}
                                  >
                                    {isCampaignExpanded ? (
                                      <ExpandMoreIcon color="primary" />
                                    ) : (
                                      <ChevronRightIcon color="action" />
                                    )}
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ ml: 1 }}>
                                      {campaign.name} ({campaign.skills.length} skills)
                                    </Typography>
                                  </Box>

                                  {/* Skills dentro de la campaña */}
                                  {isCampaignExpanded && (
                                    <Box sx={{ ml: 4, mt: 1 }}>
                                      {campaign.skills.map(skill => {
                                        const isSkillExpanded = expandedSkills.includes(skill.id);
                                        const agentsInSkill = getAgentsInSkill(skill.id);
                                        const isDragOver = dragOverSkill === skill.id;

                                        return (
                                          <Box key={skill.id} sx={{ mb: 1 }}>
                                            {/* Header del Skill */}
                                            <Box
                                              draggable={true}
                                              onDragStart={(e) => handleSkillDragStart(e, skill)}
                                              onDragEnd={handleDragEnd}
                                              onDragOver={(e) => handleDragOver(e, skill.id)}
                                              onDragLeave={handleDragLeave}
                                              onDrop={(e) => handleDrop(e, skill.id)}
                                              onContextMenu={(e) => handleContextMenu(e, `skill_${skill.id}`)}
                                              onClick={() => toggleSkill(skill.id)}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 1,
                                                bgcolor: isDragOver ? '#e3f2fd' : 'white',
                                                border: isDragOver ? '2px solid #2196f3' : '1px solid #ddd',
                                                borderRadius: 1,
                                                cursor: 'grab',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                  bgcolor: '#f5f5f5'
                                                },
                                                '&:active': {
                                                  cursor: 'grabbing'
                                                }
                                              }}
                                            >
                                              {isSkillExpanded ? (
                                                <ExpandMoreIcon fontSize="small" color="primary" />
                                              ) : (
                                                <ChevronRightIcon fontSize="small" color="action" />
                                              )}
                                              <SkillIcon fontSize="small" sx={{ ml: 1, mr: 1 }} color={isSkillExpanded ? 'primary' : 'action'} />
                                              <Typography variant="body2" fontWeight={isSkillExpanded ? 600 : 400}>
                                                {skill.skillname}
                                              </Typography>
                                              <Chip
                                                size="small"
                                                label={`${agentsInSkill.length}`}
                                                sx={{ ml: 'auto', height: 20 }}
                                              />
                                            </Box>

                                            {/* Agentes del skill */}
                                            {isSkillExpanded && (
                                              <Box sx={{ ml: 6, mt: 1 }}>
                                                {agentsInSkill.length === 0 ? (
                                                  <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontStyle: 'italic' }}>
                                                    Sin agentes
                                                  </Typography>
                                                ) : (
                                                  agentsInSkill.map(agent => {
                                                    const skillContext = `skill_${skill.id}`;
                                                    const isSelected = getSelectedAgents(skillContext).includes(agent.agent);
                                                    return (
                                                      <Box
                                                        key={agent.agent}
                                                        draggable={true}
                                                        onDragStart={(e) => handleDragStart(e, agent, skillContext, skill.id)}
                                                        onDragEnd={handleDragEnd}
                                                        onClick={(e) => {
                                                          if (!e.target.closest('.delete-icon')) {
                                                            toggleAgentSelectionDrag(agent.agent, skillContext, e.shiftKey);
                                                          }
                                                        }}
                                                        sx={{
                                                          position: 'relative',
                                                          p: 0.75,
                                                          pl: 1.5,
                                                          mb: 0.5,
                                                          bgcolor: isSelected ? 'rgba(33, 150, 243, 0.08)' : 'white',
                                                          borderLeft: isSelected ? '4px solid #2196f3' : '4px solid transparent',
                                                          border: '1px solid #e0e0e0',
                                                          borderRadius: 1,
                                                          cursor: 'pointer',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 1,
                                                          transition: 'all 0.2s ease',
                                                          '&:hover': {
                                                            bgcolor: 'rgba(33, 150, 243, 0.12)',
                                                            borderLeftColor: '#2196f3',
                                                            '& .delete-icon': {
                                                              opacity: 1
                                                            }
                                                          },
                                                          '&:active': {
                                                            cursor: 'grabbing'
                                                          }
                                                        }}
                                                      >
                                                        <PersonIcon
                                                          fontSize="small"
                                                          sx={{ color: isSelected ? '#2196f3' : 'action.active' }}
                                                        />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                          <Typography
                                                            variant="body2"
                                                            noWrap
                                                            fontSize="0.875rem"
                                                            sx={{ fontWeight: isSelected ? 500 : 400 }}
                                                          >
                                                            {agent.name}
                                                          </Typography>
                                                          <Typography variant="caption" color="text.secondary" noWrap fontSize="0.75rem">
                                                            {agent.agent}
                                                          </Typography>
                                                        </Box>
                                                        {canAction('remove_skills_from_agents') && (
                                                        <IconButton
                                                          className="delete-icon"
                                                          size="small"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveAgentFromSkill(agent, skill.id);
                                                          }}
                                                          disabled={processing}
                                                          sx={{
                                                            opacity: 0,
                                                            transition: 'opacity 0.2s',
                                                            '&:hover': {
                                                              color: 'error.main'
                                                            }
                                                          }}
                                                        >
                                                          <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                        )}
                                                      </Box>
                                                    );
                                                  })
                                                )}
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}

                            {getSkillsGroupedByCampaign().length === 0 && (
                              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                                No se encontraron skills
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Vista Kanban */}
                        {skillViewMode === 'kanban' && (
                          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
                            {getFilteredSkillsForDrag().map(skill => {
                              const agentsInSkill = getAgentsInSkill(skill.id);
                              const isDragOver = dragOverSkill === skill.id;

                              return (
                                <Card
                                  key={skill.id}
                                  variant="outlined"
                                  onDragOver={(e) => handleDragOver(e, skill.id)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, skill.id)}
                                  onContextMenu={(e) => handleContextMenu(e, `skill_${skill.id}`)}
                                  sx={{
                                    minWidth: 280,
                                    maxWidth: 280,
                                    bgcolor: isDragOver ? '#e3f2fd' : 'white',
                                    border: isDragOver ? '2px solid #2196f3' : '1px solid #ddd',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      boxShadow: 2
                                    }
                                  }}
                                >
                                  <CardContent sx={{ p: 2 }}>
                                    {/* Header del skill */}
                                    <Box sx={{ mb: 2 }}>
                                      <Box
                                        draggable={true}
                                        onDragStart={(e) => handleSkillDragStart(e, skill)}
                                        onDragEnd={handleDragEnd}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          mb: 0.5,
                                          cursor: 'grab',
                                          '&:active': {
                                            cursor: 'grabbing'
                                          }
                                        }}
                                      >
                                        <SkillIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                          {skill.skillname}
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        {skill.campaign_name || 'Sin Campaña'}
                                      </Typography>
                                      <Divider sx={{ mt: 1 }} />
                                    </Box>

                                    {/* Lista de agentes */}
                                    <Box sx={{ minHeight: 200, maxHeight: 'calc(70vh - 150px)', overflow: 'auto' }}>
                                      {agentsInSkill.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4, fontStyle: 'italic' }}>
                                          Sin agentes
                                        </Typography>
                                      ) : (
                                        agentsInSkill.map(agent => {
                                          const skillContext = `skill_${skill.id}`;
                                          const isSelected = getSelectedAgents(skillContext).includes(agent.agent);
                                          return (
                                            <Box
                                              key={agent.agent}
                                              draggable={true}
                                              onDragStart={(e) => handleDragStart(e, agent, skillContext, skill.id)}
                                              onDragEnd={handleDragEnd}
                                              onClick={(e) => {
                                                if (!e.target.closest('.delete-icon')) {
                                                  toggleAgentSelectionDrag(agent.agent, skillContext, e.shiftKey);
                                                }
                                              }}
                                              sx={{
                                                position: 'relative',
                                                p: 1,
                                                pl: 1.5,
                                                mb: 1,
                                                bgcolor: isSelected ? 'rgba(33, 150, 243, 0.08)' : '#fafafa',
                                                borderLeft: isSelected ? '4px solid #2196f3' : '4px solid transparent',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                  bgcolor: 'rgba(33, 150, 243, 0.12)',
                                                  borderLeftColor: '#2196f3',
                                                  boxShadow: 1,
                                                  '& .delete-icon': {
                                                    opacity: 1
                                                  }
                                                },
                                                '&:active': {
                                                  cursor: 'grabbing'
                                                }
                                              }}
                                            >
                                              <PersonIcon
                                                fontSize="small"
                                                sx={{ color: isSelected ? '#2196f3' : 'action.active' }}
                                              />
                                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                  variant="body2"
                                                  noWrap
                                                  fontSize="0.875rem"
                                                  sx={{ fontWeight: isSelected ? 500 : 400 }}
                                                >
                                                  {agent.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap fontSize="0.75rem">
                                                  {agent.agent}
                                                </Typography>
                                              </Box>
                                              {canAction('remove_skills_from_agents') && (
                                              <IconButton
                                                className="delete-icon"
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRemoveAgentFromSkill(agent, skill.id);
                                                }}
                                                disabled={processing}
                                                sx={{
                                                  opacity: 0,
                                                  transition: 'opacity 0.2s',
                                                  '&:hover': {
                                                    color: 'error.main'
                                                  }
                                                }}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                              )}
                                            </Box>
                                          );
                                        })
                                      )}
                                    </Box>

                                    {/* Footer con contador */}
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {agentsInSkill.length} agente{agentsInSkill.length !== 1 ? 's' : ''}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              );
                            })}

                            {getFilteredSkillsForDrag().length === 0 && (
                              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4, width: '100%' }}>
                                No se encontraron skills
                              </Typography>
                            )}
                          </Box>
                        )}

                        {getFilteredSkillsForDrag().length === 0 && skillViewMode === 'grid' && (
                          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                            No se encontraron skills
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Menú Contextual */}
      <Menu
        open={contextMenu.visible}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.visible
            ? { top: contextMenu.y, left: contextMenu.x }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            handleCopy(contextMenu.context);
            closeContextMenu();
          }}
          disabled={getSelectedAgents(contextMenu.context).length === 0}
        >
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copiar ({getSelectedAgents(contextMenu.context).length})</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCut(contextMenu.context);
            closeContextMenu();
          }}
          disabled={getSelectedAgents(contextMenu.context).length === 0 || !contextMenu.skillId}
        >
          <ListItemIcon>
            <CutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cortar ({getSelectedAgents(contextMenu.context).length})</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handlePaste(contextMenu.skillId);
            closeContextMenu();
          }}
          disabled={!clipboard.agents || clipboard.agents.length === 0 || !contextMenu.skillId}
        >
          <ListItemIcon>
            <PasteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            Pegar ({clipboard.agents ? clipboard.agents.length : 0})
          </ListItemText>
        </MenuItem>

        <Divider />

        {canAction('remove_skills_from_agents') && (
        <MenuItem
          onClick={() => {
            handleDeleteSelected(contextMenu.context);
            closeContextMenu();
          }}
          disabled={getSelectedAgents(contextMenu.context).length === 0}
          sx={{ color: 'secondary.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>Eliminar ({getSelectedAgents(contextMenu.context).length})</ListItemText>
        </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnack} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
